import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hoaApi, noticesApi, maintenanceApi, eventsApi, parcelsApi, paymentsApi, chatApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import BuildingSwitcher from '../components/BuildingSwitcher';
import RoleSwitcher from '../components/RoleSwitcher';
import {
  Building2,
  Bell,
  FileText,
  Wrench,
  Calendar,
  CreditCard,
  Users,
  Package,
  MessageSquare,
  Settings,
  LogOut,
  Home,
  Search,
  Plus,
  ChevronRight,
  AlertCircle,
  Clock,
  MapPin,
  QrCode,
  DollarSign,
  TrendingUp,
  ShoppingBag,
  User,
  Shield,
  PieChart,
  CalendarDays,
  Vote,
} from 'lucide-react';

const navItems = [
  { icon: Home, label: 'Home', path: '/dashboard' },
  { icon: Bell, label: 'Notices', path: '/dashboard/notices' },
  { icon: Wrench, label: 'Maintenance', path: '/dashboard/maintenance' },
  { icon: FileText, label: 'Documents', path: '/dashboard/documents' },
  { icon: Calendar, label: 'Bookings', path: '/dashboard/bookings' },
  { icon: Calendar, label: 'Venue Bookings', path: '/dashboard/venue-bookings' },
  { icon: CalendarDays, label: 'Calendar', path: '/dashboard/calendar' },
  { icon: PieChart, label: 'Finances', path: '/dashboard/financial-dashboard' },
  { icon: User, label: 'Profile', path: '/dashboard/profile' },
];

export default function ResidentDashboard() {
  const { user, logout, loading: authLoading, activeBuilding } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [pendingParcels, setPendingParcels] = useState(0);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);

  // Redirect non-residents to their appropriate dashboard
  useEffect(() => {
    if (!authLoading && user) {
      console.log('ResidentDashboard redirect check - user role:', user.role);
      if (user.role === 'platform_owner') {
        navigate('/platform', { replace: true });
        return;
      }
      if (['managing_agent', 'agent_admin'].includes(user.role)) {
        navigate('/agent', { replace: true });
        return;
      }
      if (['property_manager', 'trustee'].includes(user.role)) {
        navigate('/admin', { replace: true });
        return;
      }
    }
  }, [user, authLoading, navigate]);

  // Resolve the active building (multi-building model: user.hoa_id may be null;
  // activeBuilding from /me/buildings is the authoritative source).
  const hoaId = user?.hoa_id || activeBuilding?.hoa_id || null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // No building linked: show empty placeholder dashboard.
        if (!hoaId) {
          setNotices([]);
          setEvents([]);
          setTickets([]);
          setPendingParcels(0);
          setPendingInvoices([]);
          setUnreadChat(0);
          setStats(null);
          setLoading(false);
          return;
        }

        const [noticesData, eventsData, ticketsData, parcelsData, invoicesData, chatUnread] = await Promise.all([
          noticesApi.list(hoaId).catch(() => []),
          eventsApi.list(hoaId).catch(() => []),
          maintenanceApi.list(hoaId).catch(() => []),
          parcelsApi.getPendingCount(hoaId).catch(() => ({ pending_count: 0 })),
          paymentsApi.listInvoices(hoaId, 'pending').catch(() => []),
          chatApi.unreadCount(hoaId).catch(() => ({ unread: 0 })),
        ]);

        setNotices(noticesData.slice(0, 5));
        setEvents(eventsData.slice(0, 3));
        setTickets(ticketsData.filter(t => t.reported_by === user.user_id).slice(0, 3));
        setPendingParcels(parcelsData.pending_count);
        setPendingInvoices(invoicesData.slice(0, 2));
        setUnreadChat(chatUnread?.unread || 0);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [hoaId, user?.user_id]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getCategoryColor = (category) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700',
      maintenance: 'bg-amber-100 text-amber-700',
      event: 'bg-sky-100 text-sky-700',
      general: 'bg-slate-100 text-slate-700',
    };
    return colors[category] || colors.general;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-sky-100 text-sky-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-sky-100 text-sky-700">
                {user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm text-slate-500">Welcome back</p>
              <p className="font-semibold font-manrope text-slate-900">{user?.name || 'Resident'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <BuildingSwitcher compact />
            </div>
            <RoleSwitcher />
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5 text-slate-600" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-gradient-to-br from-sky-500 to-sky-600 border-0 text-white" data-testid="parcels-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sky-100 text-sm">Pending Parcels</p>
                  <p className="text-3xl font-bold font-manrope">{pendingParcels}</p>
                </div>
                <Package className="w-10 h-10 text-sky-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 text-white" data-testid="invoices-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Due Invoices</p>
                  <p className="text-3xl font-bold font-manrope">{pendingInvoices.length}</p>
                </div>
                <DollarSign className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: Vote, label: 'Vote', path: '/dashboard/voting', color: 'bg-indigo-50 text-indigo-600' },
            { icon: Wrench, label: 'Report', path: '/dashboard/maintenance/new', color: 'bg-amber-50 text-amber-600' },
            { icon: QrCode, label: 'Visitors', path: '/dashboard/visitors', color: 'bg-sky-50 text-sky-600' },
            { icon: Calendar, label: 'Book', path: '/dashboard/facilities', color: 'bg-purple-50 text-purple-600' },
          ].map((action) => (
            <Link key={action.label} to={action.path}>
              <div className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-slate-100 transition-colors" data-testid={`quick-action-${action.label.toLowerCase()}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${action.color}`}>
                  <action.icon className="w-6 h-6" />
                </div>
                <span className="text-xs font-medium text-slate-600">{action.label}</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Community Chat Card */}
        <Card
          className="cursor-pointer hover:shadow-md transition-all border-slate-200"
          onClick={() => navigate('/dashboard/chat')}
          data-testid="community-chat-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                  {unreadChat > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center ring-2 ring-white"
                      data-testid="community-unread-badge"
                    >
                      {unreadChat > 99 ? '99+' : unreadChat}
                    </span>
                  )}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Community Chat</div>
                  <div className="text-sm text-slate-500" data-testid="community-unread-label">
                    {unreadChat > 0
                      ? `${unreadChat} new ${unreadChat === 1 ? 'message' : 'messages'}`
                      : "You're all caught up"}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        {/* Trustee Hub - Only visible to trustees */}
        {(user?.role === 'trustee' || user?.is_trustee === true) && (
          <Card 
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate('/trustee-hub')}
            data-testid="trustee-hub-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Trustee Hub</h3>
                    <p className="text-indigo-200 text-sm">Manage tasks & communicate with agents</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Notices */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold font-manrope text-slate-900">Recent Notices</h2>
            <Link to="/dashboard/notices" className="text-sm text-sky-600 font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {loading ? (
              <>
                <Skeleton className="h-20 rounded-xl" />
                <Skeleton className="h-20 rounded-xl" />
              </>
            ) : notices.length > 0 ? (
              notices.slice(0, 3).map((notice, index) => (
                <Card 
                  key={notice.notice_id} 
                  className="overflow-hidden animate-slideUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`notice-card-${notice.notice_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getCategoryColor(notice.category)}`}>
                        <Bell className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{notice.title}</p>
                        <p className="text-sm text-slate-500 line-clamp-2">{notice.description}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatDate(notice.created_at)}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-6 text-center text-slate-500">
                  No recent notices
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Upcoming Events */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold font-manrope text-slate-900">Upcoming Events</h2>
            <Link to="/dashboard/events" className="text-sm text-sky-600 font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {loading ? (
              <Skeleton className="h-24 rounded-xl" />
            ) : events.length > 0 ? (
              events.slice(0, 2).map((event, index) => (
                <Card 
                  key={event.event_id} 
                  className="overflow-hidden animate-slideUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`event-card-${event.event_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-sky-50 text-sky-600">
                        <span className="text-xs font-medium">{formatDate(event.start_time).split(' ')[0]}</span>
                        <span className="text-xl font-bold">{new Date(event.start_time).getDate()}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{event.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-sm text-slate-500">{formatTime(event.start_time)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-500">{event.location}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-6 text-center text-slate-500">
                  No upcoming events
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* My Maintenance Tickets */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold font-manrope text-slate-900">My Tickets</h2>
            <Link to="/dashboard/maintenance" className="text-sm text-sky-600 font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {loading ? (
              <Skeleton className="h-20 rounded-xl" />
            ) : tickets.length > 0 ? (
              tickets.map((ticket, index) => (
                <Card 
                  key={ticket.ticket_id} 
                  className="overflow-hidden animate-slideUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`ticket-card-${ticket.ticket_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                          <Wrench className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{ticket.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className={`text-xs ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                            <span className="text-xs text-slate-400">{ticket.category}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-slate-500 mb-3">No active tickets</p>
                  <Link to="/dashboard/maintenance/new">
                    <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800">
                      <Plus className="w-4 h-4 mr-1" />
                      Report Issue
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Pending Payments */}
        {pendingInvoices.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold font-manrope text-slate-900">Due Payments</h2>
              <Link to="/dashboard/payments" className="text-sm text-sky-600 font-medium">
                View All
              </Link>
            </div>
            <Card className="overflow-hidden border-amber-200 bg-amber-50/50" data-testid="pending-payments-card">
              <CardContent className="p-4">
                {pendingInvoices.slice(0, 1).map((invoice) => (
                  <div key={invoice.invoice_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{invoice.description}</p>
                        <p className="text-sm text-slate-500">Due {formatDate(invoice.due_date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{invoice.currency} {invoice.amount}</p>
                      <Link to="/dashboard/payments">
                        <Button size="sm" className="mt-1 h-8 px-3 rounded-full bg-sky-500 hover:bg-sky-600">
                          Pay Now
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 glass border-t border-slate-200/50 z-50" data-testid="bottom-nav">
        <div className="max-w-md mx-auto flex items-center justify-around py-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${
                  isActive ? 'text-sky-600' : 'text-slate-400 hover:text-slate-600'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className={`w-6 h-6 ${isActive ? 'text-sky-600' : ''}`} />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
